import { useEffect, useRef, useState } from "react";
import {
  addExclusionRule, checkExclusionRule, listExclusionRules, listManufacturers, removeExclusionRule,
  type ExclusionCheck, type ExclusionRule, type ManufacturerRow,
} from "./actions";
import { downloadCsv } from "./csv";

// Exclusions: brands a vendor asked us to stop selling, per feed. Rules added here apply to the Sheet's "Excluded brand" filter and
// counts at once, and to each feed's next ingest (which sets the stored flag). Matching is EXACT (ignoring case): "Ansel" does not match
// "Ansell", so the preview shows how many stored products a rule matches before it is saved, and the similar spellings it would miss.
// Built-in rules (D&H brands, Kroll categories) are code and are listed here read-only. Removing a console rule deactivates it; nothing is deleted.

const csvCell = (v: unknown) => `"${String(v ?? "").replace(/"/g, '""')}"`;

export function ExclusionsPanel() {
  const [rows, setRows] = useState<ExclusionRule[]>([]);
  const [feeds, setFeeds] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [feed, setFeed] = useState("ndc");
  const [value, setValue] = useState("");
  const [note, setNote] = useState("");
  const [urgency, setUrgency] = useState("24h");
  const [check, setCheck] = useState<ExclusionCheck | null>(null);
  const [checking, setChecking] = useState(false);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [showInactive, setShowInactive] = useState(false);
  const seq = useRef(0);
  const [tab, setTab] = useState<"rules" | "names">("rules");
  const [nameFeed, setNameFeed] = useState("");
  const [nameQ, setNameQ] = useState("");
  const [names, setNames] = useState<ManufacturerRow[]>([]);
  const [nameInfo, setNameInfo] = useState<{ total: number; truncated: boolean; updated: Record<string, string>; missing: string[] } | null>(null);
  const [namesBusy, setNamesBusy] = useState(false);
  const nseq = useRef(0);

  // The manufacturer browser: a loose search over every name the feeds carry (re-run a moment after typing stops).
  useEffect(() => {
    if (tab !== "names") return;
    const mine = ++nseq.current;
    const t = setTimeout(async () => {
      setNamesBusy(true);
      const r = await listManufacturers(nameFeed, nameQ.trim());
      if (mine !== nseq.current) return;
      setNamesBusy(false);
      if (!r.ok) { setError(r.error); return; }
      setError(null);
      setNames(r.data.rows);
      setNameInfo({ total: r.data.total, truncated: r.data.truncated, updated: r.data.updated, missing: r.data.missing });
    }, 300);
    return () => clearTimeout(t);
  }, [tab, nameFeed, nameQ]);

  const load = async () => {
    setLoading(true);
    const r = await listExclusionRules();
    setLoading(false);
    if (!r.ok) { setError(r.error); return; }
    setError(null);
    setRows(r.data.rows);
    setFeeds(r.data.feeds);
  };
  useEffect(() => { load(); }, []);

  // Live preview, a moment after the typing stops.
  useEffect(() => {
    setCheck(null);
    const v = value.trim();
    if (v.length < 2) return;
    const mine = ++seq.current;
    const t = setTimeout(async () => {
      setChecking(true);
      const r = await checkExclusionRule(feed, v);
      if (mine !== seq.current) return;
      setChecking(false);
      if (r.ok) setCheck(r.data); else setError(r.error);
    }, 450);
    return () => clearTimeout(t);
  }, [feed, value]);

  const save = async () => {
    setSaving(true);
    setMessage(null);
    const r = await addExclusionRule(feed, value.trim(), note.trim(), urgency);
    setSaving(false);
    if (!r.ok) { setError(r.error); return; }
    setError(null);
    setMessage(r.data.warning || `Rule saved: ${r.data.matches.toLocaleString()} stored product${r.data.matches === 1 ? "" : "s"} match. A request is open on the Requests page (console, WooCommerce, Merchant Center).`);
    setValue(""); setNote("");
    await load();
  };

  const remove = async (id: string) => {
    const r = await removeExclusionRule(id);
    if (!r.ok) { setError(r.error); return; }
    setMessage("Rule deactivated (kept for the record).");
    await load();
  };

  const shown = rows.filter((r) => showInactive || r.active);
  const canSave = value.trim().length >= 2 && !saving && !(check?.duplicate);

  return (
    <div className="sheet-page">
      <div className="sheet-top">
        <div className="sheet-title">
          <h1>Exclusions</h1>
          <span className="meta">
            Brands a vendor asked us to stop selling, per feed. Matching is exact, ignoring case. A rule shows in the Sheet's Excluded brand
            filter immediately, and each feed's next ingest sets the stored flag. Excluded products stay in the catalog for audit; only the sync out filters them.
          </span>
        </div>

        <div className="panel" style={{ margin: 0 }}>
          <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
            <select value={feed} onChange={(e) => setFeed(e.target.value)} aria-label="Feed">
              {Object.entries(feeds).length === 0 ? <option value="ndc">NDC</option> :
                Object.entries(feeds).map(([k, n]) => <option key={k} value={k}>{n}</option>)}
            </select>
            <input className="sheet-search" style={{ maxWidth: 360 }} placeholder="Manufacturer / brand name, as the feed spells it"
                   value={value} onChange={(e) => { setValue(e.target.value); setMessage(null); }} />
            <input style={{ maxWidth: 260 }} placeholder="Who asked / note (optional)" value={note} onChange={(e) => setNote(e.target.value)} />
            <select value={urgency} onChange={(e) => setUrgency(e.target.value)} aria-label="How soon it must be done" title="Opens a request with this due time, tracked on the Requests page">
              <option value="immediate">Immediate (due in 1 hour)</option><option value="24h">Within 24 hours</option><option value="48h">Within 48 hours</option>
            </select>
            <button type="button" disabled={!canSave} onClick={save}>{saving ? "Saving…" : "Add rule"}</button>
          </div>
          <div className="meta" style={{ marginTop: 10, minHeight: 22 }}>
            {value.trim().length < 2 ? "Type a name to preview what it matches." :
              checking || !check ? "Checking…" :
              check.duplicate ? "This rule already exists." :
              check.matches > 0
                ? <>Matches <b>{check.matches.toLocaleString()}</b> stored product{check.matches === 1 ? "" : "s"}
                    {check.spellings.length > 1 ? ` (${check.spellings.map((s) => `${s.value}: ${s.count}`).join("; ")})` : ""}.</>
                : <b style={{ color: "var(--warn)" }}>Matches 0 stored products: the name may be misspelled.</b>}
            {check && check.similar.length > 0 && (
              <span> Similar names this rule would NOT match: {check.similar.map((s, i) => (
                <span key={s.value}>{i ? ", " : " "}
                  <button type="button" className="link" style={{ all: "unset", cursor: "pointer", textDecoration: "underline" }}
                          onClick={() => setValue(s.value)}>{s.value} ({s.count.toLocaleString()})</button></span>))}.
              </span>
            )}
          </div>
          {message && <div className="meta" style={{ marginTop: 6 }}>{message}</div>}
        </div>
        {error && <div className="panel error sheet-error">{error}</div>}
        <div className="tabs">
          <button className={tab === "rules" ? "active" : ""} onClick={() => setTab("rules")}>Rules</button>
          <button className={tab === "names" ? "active" : ""} onClick={() => setTab("names")}>Find manufacturers and brands</button>
        </div>
        {tab === "names" && (
          <div className="sheet-actions" style={{ justifyContent: "flex-start" }}>
            <select value={nameFeed} onChange={(e) => setNameFeed(e.target.value)} aria-label="Feed">
              <option value="">All feeds</option>
              {Object.entries(feeds).filter(([k]) => k !== "nms").map(([k, n]) => <option key={k} value={k}>{n}</option>)}
            </select>
            <input className="sheet-search" placeholder="Loose search: part of a name, e.g. intel, omron, cyber" value={nameQ}
                   onChange={(e) => setNameQ(e.target.value)} />
            <span className="meta" style={{ margin: 0 }}>
              {namesBusy ? "Loading…" : nameInfo ? `${nameInfo.total.toLocaleString()} name${nameInfo.total === 1 ? "" : "s"}${nameInfo.truncated ? " (first 1,000 shown; narrow the search)" : ""}` : ""}
              {nameInfo && nameInfo.missing.length ? ` · no list yet for ${nameInfo.missing.join(", ")} (built by that feed's next catalog run)` : ""}
              {nameInfo && Object.keys(nameInfo.updated).length ? ` · list from ${Object.values(nameInfo.updated).sort()[0]?.slice(0, 10)}` : ""}
            </span>
            <button type="button" className="sheet-export" style={{ marginLeft: "auto" }} disabled={!names.length}
                    onClick={() => downloadCsv("hec-manufacturers.csv", [["feed", "manufacturer / brand", "products", "excluded by a rule"],
                      ...names.map((r) => [r.feed, r.name, r.count, r.excluded ? "yes" : ""])].map((l) => l.map(csvCell).join(",")).join("\r\n"))}>
              Export list ({names.length})
            </button>
          </div>
        )}
        {tab === "rules" && <div className="sheet-actions">
          <label className="meta" style={{ margin: "0 auto 0 0" }}>
            <input type="checkbox" checked={showInactive} onChange={(e) => setShowInactive(e.target.checked)} /> show removed rules
          </label>
          <button type="button" className="sheet-export" disabled={!shown.length}
                  onClick={() => downloadCsv("hec-exclusion-rules.csv", [["feed", "type", "value", "origin", "active", "matches now", "note"],
                    ...shown.map((r) => [r.feed, r.kind, r.value, r.origin, r.active ? "yes" : "no", r.matches ?? "", r.note ?? ""])]
                    .map((l) => l.map(csvCell).join(",")).join("\r\n"))}>
            Export rules ({shown.length})
          </button>
        </div>}
      </div>

      {tab === "names" ? (
      <div className="sheet-scroll">
        <table className="manual-products sheet-table feed-table">
          <thead><tr><th>Feed</th><th>Manufacturer / brand (as the feed spells it)</th><th>Products</th><th>Rule</th><th></th></tr></thead>
          <tbody>
            {names.map((r) => (
              <tr key={r.sourceId + "|" + r.name} style={{ cursor: "default" }}>
                <td>{r.feed}</td>
                <td className="wrap">{r.name}</td>
                <td className="num">{r.count.toLocaleString()}</td>
                <td>{r.excluded ? "Excluded" : ""}</td>
                <td>{r.excluded || r.name === "(blank)" ? null : (
                  <button type="button" onClick={() => { setFeed(r.sourceId); setValue(r.name); setMessage(null); setTab("rules"); window.scrollTo(0, 0); }}>
                    Use as rule</button>)}</td>
              </tr>
            ))}
          </tbody>
        </table>
        {!names.length && !namesBusy && <p className="meta" style={{ padding: 16 }}>No names.</p>}
      </div>
      ) : (
      <div className="sheet-scroll">
        <table className="manual-products sheet-table feed-table">
          <thead><tr><th>Feed</th><th>Type</th><th>Value</th><th>Origin</th><th>Matches now</th><th>Potential matches to review (not excluded)</th><th>Status</th><th></th></tr></thead>
          <tbody>
            {shown.map((r) => (
              <tr key={r.id} style={{ cursor: "default", opacity: r.active ? 1 : 0.55 }}>
                <td>{r.feed}</td>
                <td>{r.kind}</td>
                <td className="wrap">{r.value}</td>
                <td className="wrap">{r.origin}</td>
                <td className="num">{r.matches == null ? (r.note || "n/a") : r.matches.toLocaleString()}
                  {r.matches === 0 && r.active ? <span style={{ color: "var(--warn)" }}> (matches nothing)</span> : null}</td>
                <td className="wrap">
                  {(r.potential ?? []).length === 0 ? <span className="meta" style={{ margin: 0 }}>{r.active && r.kind === "Brand" ? "none found" : ""}</span> :
                    (r.potential ?? []).map((pm) => (
                      <div key={pm.name} style={{ display: "flex", gap: 8, alignItems: "center", justifyContent: "space-between" }}>
                        <span>{pm.name} <span className="meta" style={{ margin: 0 }}>({pm.count.toLocaleString()})</span></span>
                        <button type="button" onClick={() => { setFeed(r.sourceId); setValue(pm.name); setMessage(null); window.scrollTo(0, 0); }}>Use as rule</button>
                      </div>))}
                </td>
                <td>{r.active ? "Active" : "Removed"}</td>
                <td>{r.removable ? <button type="button" onClick={() => remove(r.id)}>Remove</button> : null}</td>
              </tr>
            ))}
          </tbody>
        </table>
        {shown.length === 0 && !loading && <p className="meta" style={{ padding: 16 }}>No rules.</p>}
        {loading && <p className="meta" style={{ padding: 16 }}>Loading…</p>}
      </div>
      )}
    </div>
  );
}
